import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import YearSelect from '../../../components/YearSelect';
import { useHolidays } from './useHolidays';
import HolidayDialog from './HolidayDialog';
import HolidayTable from './HolidayTable';
import HolidayTableSkeleton from './HolidayTableSkeleton';
import DeleteConfirmationDialog from '../../../components/DeleteConfirmationDialog';

interface Holiday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description?: string;
  isActive: boolean;
}

interface HolidayManagementProps {
  isAdmin: boolean;
  onHolidaysChange?: () => void;
}

const HolidayManagement: React.FC<HolidayManagementProps> = ({ isAdmin, onHolidaysChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const {
    holidays,
    loading,
    error,
    success,
    handleCreateHoliday,
    handleUpdateHoliday,
    handleDeleteHoliday,
    openDeleteDialog,
    closeDeleteDialog,
    deleteDialogOpen,
    selectedHoliday,
    isDeleting,
  } = useHolidays(isAdmin, selectedYear);

  const handleOpenDialog = (holiday?: Holiday) => {
    setEditingHoliday(holiday || null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingHoliday(null);
  };

  const handleSubmit = async (data: any) => {
    if (editingHoliday) {
      await handleUpdateHoliday(editingHoliday.id, data);
    } else {
      await handleCreateHoliday(data);
    }
    // Notify parent component that holidays have changed
    onHolidaysChange?.();
  };

  if (!isAdmin) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            Access denied. Only administrators can manage holidays.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <HolidayTableSkeleton />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" gutterBottom>Holiday Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage non-working days that will be excluded from time-off calculations.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <YearSelect value={selectedYear} onChange={setSelectedYear} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Holiday
            </Button>
          </Box>
        </Box>

        {/* Messages */}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Table */}
        <HolidayTable
          holidays={holidays}
          onEdit={handleOpenDialog}
          onDelete={(holiday) => {
            openDeleteDialog(holiday);
          }}
        />

        {/* Dialog */}
        <HolidayDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
          editingHoliday={editingHoliday}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={closeDeleteDialog}
          onConfirm={handleDeleteHoliday}
          title="Delete Holiday"
          itemName={selectedHoliday?.name || ''}
          itemType="holiday"
          loading={isDeleting}
        />
      </CardContent>
    </Card>
  );
};

export default HolidayManagement;
